import React, { useEffect, useState } from 'react';
// Types
import { FormInstance } from 'antd/es/form';
import {
  PromoCodesChargeSubject,
  ProductTypesType,
} from 'api/graphql-global-types';
// Helpers
import { formItemRules } from './form-item-rules';
import {
  STREAM_DISCOUNT_OPTIONS,
  amountFormatter,
  amountParser,
  isStreamDiscountAllowed,
} from './discont-amount-helpers';
// Constants
import { TIME_ZONE_OPTIONS } from 'constants/timeZone';
// Context
import { useStoresSelectProducts } from 'context/StoresSelectedProducts';
// UI
import {
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Radio,
  RadioChangeEvent,
  Select,
  Space,
  Switch,
} from 'antd';
// Components
import ChipSelect from 'components/common/ChipSelect/ChipSelect';
import FormItemLabel from '../FormItemLabel/FormItemLabel';
import StoreDataSelect from '../StoreDataSelect/StoreDataSelect';
import SiteWideData, {
  ALL_SITE_WIDE_KEYS,
  INTERNAL_SITE_WIDE_KEYS,
} from '../SiteWideData/SiteWideData';

import './styles.scss';

const { RangePicker } = DatePicker;
const { Option } = Select;

const formItemLayout = {
  labelCol: { span: 9 },
  wrapperCol: { span: 12 },
};

const DATEPICKER_DATE_FORMAT = 'MMMM D, YYYY';

// TODO: delete filter when Shoutout feature will be turned on
const initialSiteWideOptions = Object.values(ProductTypesType).filter(
  (item) => item !== ProductTypesType.Shoutout
);

const initialFormValues = {
  name: null,
  startDate: null,
  validityPeriod: [null, null],
  timeZone: TIME_ZONE_OPTIONS[0].tzCode,
  amount: null,
  usageLimit: null,
  perUserLimit: null,
  chargeSubject: PromoCodesChargeSubject.Store,
  isSiteWide: true,
  storeProducts: [],
  siteWideOptions: initialSiteWideOptions,
};

const chargeSubjectOptions = [
  {
    label: 'Store',
    value: PromoCodesChargeSubject.Store,
  },
  {
    label: 'Platform',
    value: PromoCodesChargeSubject.Platform,
  },
  {
    label: 'Internal',
    value: PromoCodesChargeSubject.Internal,
  },
];

const scopeOptions = [
  { label: 'All stores (site-wide)', value: true },
  { label: 'Specific stores', value: false },
];

export type CreatePromoCodeFormProps = {
  formInstance?: FormInstance;
};

const CreatePromoCodeForm: React.FC<CreatePromoCodeFormProps> = ({
  formInstance,
}) => {
  const [formLocal] = Form.useForm();

  const form = formInstance || formLocal;
  const { selectedStoreItems, flushSelected } = useStoresSelectProducts();
  const [chargingOption, setChargingOption] = useState(
    PromoCodesChargeSubject.Store
  );
  const chargeTooltipTitle =
    chargingOption === PromoCodesChargeSubject.Internal
      ? 'If Internal is selected, no revenues or profit is recorded for Platform and Store'
      : "If Store selected the system will charge the store's profit for the discount. If chosen Platform, then the system will charge the platform's margin for the discount.";

  const [isSiteWide, setIsSiteWide] = useState<boolean>(
    initialFormValues.isSiteWide
  );
  const [siteWideKeys, setSiteWideKeys] =
    useState<string[]>(ALL_SITE_WIDE_KEYS);
  const [timeLimited, setTimeLimited] = useState<boolean>(false);
  const [codeUsageLimited, setCodeUsageLimited] = useState<boolean>(false);
  const [perUserUsageLimited, setPerUserUsageLimited] =
    useState<boolean>(false);

  const isInternal = chargingOption === PromoCodesChargeSubject.Internal;
  const coversStreams = isSiteWide
    ? siteWideKeys.includes(ProductTypesType.Stream)
    : Object.values(selectedStoreItems).some((items) =>
        items.some((item) => String(item?.value).startsWith('Stream|'))
      );
  // The backend exempts Internal codes because it forces them to 100%.
  const requiresStreamPreset = coversStreams && !isInternal;

  useEffect(() => {
    if (
      requiresStreamPreset &&
      !isStreamDiscountAllowed(form.getFieldValue('amount'))
    ) {
      form.setFieldsValue({ amount: null });
    }
  }, [requiresStreamPreset, form]);

  const handleTimeLimitedSwitchOnChange = (value: boolean): void => {
    setTimeLimited(value);
    form.resetFields(['startDate', 'validityPeriod']);
  };

  const handleCodeUsageLimitedCheckboxOnChange = (): void => {
    setCodeUsageLimited(!codeUsageLimited);
    form.resetFields(['limitations']);
  };

  const handlePerUserUsageLimitedCheckboxOnChange = (): void => {
    setPerUserUsageLimited(!perUserUsageLimited);
    form.resetFields(['usagePerUser']);
  };

  const handleScopeChange = (e: RadioChangeEvent): void => {
    const nextIsSiteWide = Boolean(e.target.value);

    setIsSiteWide(nextIsSiteWide);
    flushSelected();

    if (nextIsSiteWide) {
      setSiteWideKeys(
        isInternal ? INTERNAL_SITE_WIDE_KEYS : ALL_SITE_WIDE_KEYS
      );
      form.setFieldsValue({
        siteWideOptions: isInternal
          ? INTERNAL_SITE_WIDE_KEYS
          : initialSiteWideOptions,
      });
    }
  };

  const handleChargeSubjectChange = (e: RadioChangeEvent) => {
    const selectedValue = e.target.value;
    setChargingOption(selectedValue);

    if (selectedValue === PromoCodesChargeSubject.Internal) {
      form.setFieldsValue({
        amount: 100,
        siteWideOptions: INTERNAL_SITE_WIDE_KEYS,
      });
    } else {
      form.setFieldsValue({ amount: null });
    }
  };

  const scopeHelpText = isSiteWide
    ? 'Applies to every product of the selected types, in every store.'
    : 'Applies only to the items you pick from each store.';

  const amountHelpText = isInternal
    ? 'Internal codes are always 100% off and apply to streams only.'
    : requiresStreamPreset
    ? 'Streams are sold through App Store and Google Play in-app purchases, so a stream discount must be one of these presets.'
    : 'Any whole percent from 1 to 100. Including streams limits this to 1, 5, 10, 20, 30, 40, 50 or 100%.';

  return (
    <Form
      form={form}
      layout="horizontal"
      name="CreatePromoCodeForm"
      initialValues={initialFormValues}
      autoComplete="off"
      validateTrigger="onSubmit"
      {...formItemLayout}
    >
      <Divider orientation="left">Promo code</Divider>

      <Form.Item
        name="name"
        label={<FormItemLabel labelText="Name" />}
        rules={formItemRules.name}
      >
        <Input className="promo-code-form__input-medium" />
      </Form.Item>

      <Form.Item label={<FormItemLabel labelText="Time-limited" />}>
        <Switch
          onChange={handleTimeLimitedSwitchOnChange}
          checked={timeLimited}
        />
      </Form.Item>

      {timeLimited ? (
        <Form.Item
          name="validityPeriod"
          label={<FormItemLabel labelText="Validity Period" />}
          rules={formItemRules.validityPeriod}
        >
          <RangePicker />
        </Form.Item>
      ) : (
        <Form.Item
          name="startDate"
          label={<FormItemLabel labelText="Start date" />}
          rules={formItemRules.startDate}
        >
          <DatePicker
            className="promo-code-form__input-medium"
            format={DATEPICKER_DATE_FORMAT}
          />
        </Form.Item>
      )}

      <Form.Item
        name="timeZone"
        label={<FormItemLabel labelText="Time Zone" />}
        rules={formItemRules.timeZone}
      >
        <Select
          showSearch
          placeholder="Select time zone"
          optionFilterProp="children"
          className="promo-code-form__input-medium"
        >
          {TIME_ZONE_OPTIONS.map(({ tzCode, label }) => (
            <Option key={tzCode} value={tzCode}>
              {label}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Divider orientation="left">Applies to</Divider>

      <Form.Item
        name="isSiteWide"
        label={<FormItemLabel labelText="Stores" />}
        extra={scopeHelpText}
      >
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          options={scopeOptions}
          onChange={handleScopeChange}
        />
      </Form.Item>

      {isSiteWide ? (
        <SiteWideData
          rules={formItemRules.siteWideData(isSiteWide)}
          form={form}
          onChange={setSiteWideKeys}
        />
      ) : (
        <StoreDataSelect />
      )}

      <Divider orientation="left">Discount</Divider>

      <Form.Item
        name="chargeSubject"
        label={
          <FormItemLabel
            labelText="Charging"
            tooltipTitle={chargeTooltipTitle}
          />
        }
        rules={formItemRules.chargeSubject}
      >
        <Radio.Group
          options={chargeSubjectOptions}
          onChange={handleChargeSubjectChange}
        />
      </Form.Item>

      <Form.Item
        name="amount"
        label={
          <FormItemLabel
            labelText="Amount (%)"
            tooltipTitle="The percent taken off the price at checkout."
          />
        }
        rules={formItemRules.amount(requiresStreamPreset)}
        extra={amountHelpText}
      >
        {requiresStreamPreset ? (
          <ChipSelect options={STREAM_DISCOUNT_OPTIONS} />
        ) : (
          <InputNumber
            min={1}
            max={100}
            className="promo-code-form__input-small"
            formatter={amountFormatter}
            parser={amountParser}
            disabled={isInternal}
          />
        )}
      </Form.Item>

      <Divider orientation="left">Usage limits</Divider>

      <Form.Item
        name="usageLimit"
        label={
          <FormItemLabel
            labelText="Limitations"
            tooltipTitle="Enter the limitation for the code."
          />
        }
        rules={formItemRules.limitations(codeUsageLimited)}
      >
        <Space size="small" align="start">
          <InputNumber
            min={1}
            className="promo-code-form__input-small"
            disabled={!codeUsageLimited}
          />
          <Checkbox
            onChange={handleCodeUsageLimitedCheckboxOnChange}
            checked={codeUsageLimited}
          >
            Set limit
          </Checkbox>
        </Space>
      </Form.Item>

      <Form.Item
        name="perUserLimit"
        label={
          <FormItemLabel
            labelText="Usage per user"
            tooltipTitle="Enter the usage per one user."
          />
        }
        rules={formItemRules.usagePerUser(perUserUsageLimited)}
      >
        <Space size="small" align="start">
          <InputNumber
            min={1}
            className="promo-code-form__input-small"
            disabled={!perUserUsageLimited}
          />
          <Checkbox
            onChange={handlePerUserUsageLimitedCheckboxOnChange}
            checked={perUserUsageLimited}
          >
            Set limit
          </Checkbox>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default CreatePromoCodeForm;
